import { defineTour } from '../../types';

/**
 * T10 — Supplier workspace and order response (brief §7).
 *
 * Any signed-in supplier user: the portal is scoped by the server to this
 * supplier and its one buyer, and the capability flags are advisory, not
 * authority (SupplierUser.php "OPEN PRODUCT DECISION"), so no step is gated on
 * them.
 *
 * Its first part is the portal orientation (P1, W1): whom the supplier is
 * supplying, home and the orders list. It works with zero orders. Its second
 * part explains answering an order, and runs only on an order the supplier
 * opened: the guide never picks one. Without one it pauses and says so; it is
 * not marked finished. On a completed order the answer, dispatch and invoice
 * panels are gone, and those steps pause the same way.
 *
 * The guide never confirms, answers, dispatches or sends an invoice, and never
 * opens the order document.
 *
 * Copy: `guidance.tours.T10.*` — draft, for native-language review.
 */
const ORDER = {
  routeId: 'vendor.order',
  explainKey: 'guidance.tours.T10.openOrder',
} as const;

export const T10 = defineTour({
  id: 'T10',
  version: 2,
  portal: 'vendor',
  titleKey: 'guidance.tours.T10.title',
  summaryKey: 'guidance.tours.T10.summary',
  eligible: (ctx) => ctx.portal === 'vendor',
  pageRoutes: ['vendor.home', 'vendor.orders', 'vendor.order'],
  steps: [
    {
      id: 't10-context',
      anchor: ['vendor.shell.context', 'vendor.shell.identity'],
      titleKey: 'guidance.tours.T10.context.title',
      bodyKey: 'guidance.tours.T10.context.body',
    },
    {
      id: 't10-home',
      anchor: 'vendor.home.work',
      route: 'vendor.home',
      titleKey: 'guidance.tours.T10.home.title',
      bodyKey: 'guidance.tours.T10.home.body',
    },
    {
      id: 't10-orders',
      anchor: 'vendor.orders.list',
      route: 'vendor.orders',
      titleKey: 'guidance.tours.T10.orders.title',
      bodyKey: 'guidance.tours.T10.orders.body',
    },
    {
      id: 't10-order-status',
      anchor: 'vendor.order.status',
      needsRecord: ORDER,
      titleKey: 'guidance.tours.T10.orderStatus.title',
      bodyKey: 'guidance.tours.T10.orderStatus.body',
    },
    {
      id: 't10-order-answer',
      anchor: 'vendor.order.reply-answers',
      needsRecord: ORDER,
      titleKey: 'guidance.tours.T10.orderAnswer.title',
      bodyKey: 'guidance.tours.T10.orderAnswer.body',
    },
    {
      id: 't10-order-dispatch',
      anchor: 'vendor.order.dispatch',
      needsRecord: ORDER,
      titleKey: 'guidance.tours.T10.orderDispatch.title',
      bodyKey: 'guidance.tours.T10.orderDispatch.body',
    },
    {
      id: 't10-order-invoice',
      anchor: 'vendor.order.invoice',
      needsRecord: ORDER,
      titleKey: 'guidance.tours.T10.orderInvoice.title',
      bodyKey: 'guidance.tours.T10.orderInvoice.body',
    },
  ],
});
